import { useEffect, useRef, useState } from "react";
import AppTutorialButton from "@/components/AppTutorialButton";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Pencil, ChevronLeft, Settings as SettingsIcon, Moon, Sun, Utensils, Dumbbell, Sparkles, LogOut, Download, Apple, Smartphone } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n.jsx";
import { useTheme } from "@/hooks/useTheme";
import { isNativeApp } from "@/lib/native";
import { Capacitor } from "@capacitor/core";
import api from "@/lib/api";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AppsFlyerDebugModal from "@/components/AppsFlyerDebugModal";
import { ReferralCard } from "@/components/ReferralCard";

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const { theme, toggle: toggleTheme } = useTheme();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);

  // Hidden 5-tap gesture on the version footer — opens the AppsFlyer
  // in-app diagnostic panel. Since MacinCloud can't cable an iPhone to
  // Xcode's Console, this on-device UI is our only way to eyeball SDK
  // state (plugin availability, DEV_KEY/APP_ID, init status, UID) and
  // fire a live test event from the phone itself. Counter resets after
  // 1.5s of inactivity so accidental double-taps never creep towards 5.
  const versionTapCount = useRef(0);
  const versionTapTimer = useRef(null);
  const handleVersionTap = () => {
    if (versionTapTimer.current) clearTimeout(versionTapTimer.current);
    versionTapCount.current += 1;
    if (versionTapCount.current >= 5) {
      versionTapCount.current = 0;
      setDebugOpen(true);
      return;
    }
    versionTapTimer.current = setTimeout(() => {
      versionTapCount.current = 0;
    }, 1500);
  };

  useEffect(() => {
    api.get("/config/public").then(({ data }) => setCfg(data)).catch(() => {});
    // Pull the installed binary version straight from Capacitor's App
    // plugin so support requests come pre-tagged with the exact build.
    // On web / no-native we simply hide the version footer entirely.
    if (isNativeApp()) {
      import("@capacitor/app").then(({ App }) => {
        App.getInfo().then((info) => {
          setAppVersion(`${info.version} (${info.build})`);
        }).catch(() => {});
      }).catch(() => {});
    }
  }, []);

  // Include-exercise-calories toggle — hoisted from Profile so the daily
  // nutrition controls live on the primary Settings screen. `includeEx`
  // defaults to true when the field is missing on legacy accounts.
  const includeEx = user?.include_exercise_calories !== false;
  const toggleIncludeExercise = async () => {
    try {
      await api.patch("/user/preferences", { include_exercise_calories: !includeEx });
      await refresh();
    } catch {
      toast.error(lang === "en" ? "Couldn't save" : "تعذّر الحفظ");
    }
  };
  // Backend stores `calc_mode` as one of {auto, manual, beginner, advanced}
  // depending on when the user was created. Treat "manual" and legacy
  // "advanced" as manual mode; everything else as auto.
  const isManualMode = user?.calc_mode === "manual" || user?.calc_mode === "advanced";

  const waSupport = () => {
    if (!cfg?.whatsapp_number) return toast.error(lang === "en" ? "WhatsApp number not configured" : "رقم واتساب غير مضبوط");
    const num = cfg.whatsapp_number.replace(/\D/g, "");
    const msg = lang === "en"
      ? `Hi, I need help\nMy account: ${user?.email || ""}`
      : `مرحبا، أحتاج المساعدة\nحسابي: ${user?.email || ""}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // Affiliate / partnership CTA — coaches and specialists with an audience
  // tap this to open WhatsApp with a pre-filled request. Uses the same
  // support number (config first, hard-coded fallback matches MoreMenu).
  // Kept separate from `waSupport` so the pre-fill text and analytics
  // ("partnership_wa_click") can diverge without breaking either flow.

  // "ادعمنا بتقييمك" — jumps straight to the App Store / Play Store
  // write-review sheet. Uses native-app deep links when we're inside
  // the Capacitor shell (so iOS/Android can bypass the browser and
  // land inside the store app), and https:// fallbacks everywhere else
  // (browser, WhatsApp preview, desktop).
  //
  // NOTE: We only expose this affordance INSIDE the native iOS/Android
  // shell — hiding it on plain web because there's no App Store
  // rating loop worth opening from a browser (users would land on a
  // store product page, not a 1-tap review sheet).
  const rateOnStore = () => {
    const p = (() => { try { return Capacitor.getPlatform?.() || "web"; } catch { return "web"; } })();
    const iosDeep     = "itms-apps://itunes.apple.com/app/id6755807048?action=write-review";
    const androidDeep = "market://details?id=com.calofay.app";
    const iosWeb      = "https://apps.apple.com/app/id6755807048?action=write-review";
    const androidWeb  = "https://play.google.com/store/apps/details?id=com.calofay.app";
    // Native runtimes: the deep link is guaranteed to launch the
    // store app because it's installed on every iOS/Android device.
    if (p === "ios")     { window.location.href = iosDeep;     return; }
    if (p === "android") { window.location.href = androidDeep; return; }
    // Web fallback — pick the OS from the browser UA. iPhones seeing
    // apps.apple.com get auto-redirected to the App Store app by iOS.
    const isIOSWeb = /iPad|iPhone|iPod/.test(navigator.userAgent || "");
    window.open(isIOSWeb ? iosWeb : androidWeb, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="pb-4">
      <header className="px-5 pt-8 pb-5">
        <h1 className="text-3xl font-bold text-neutral-900" data-testid="settings-title">{t("settings.title")}</h1>
      </header>

      <div className="px-5 space-y-4">
        {/* Profile card */}
        <button
          onClick={() => nav("/app/settings/profile")}
          className="w-full bg-white border-2 border-brand-500 rounded-3xl p-4 flex items-center justify-between"
          data-testid="settings-profile-card"
        >
          <div className="w-11 h-11 rounded-full orange-gradient text-white flex items-center justify-center flex-shrink-0" data-testid="settings-profile-gear">
            <SettingsIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 mx-3 text-start min-w-0">
            <div className="font-bold text-neutral-900" data-testid="settings-profile-name">
              {user?.full_name || (lang === "en" ? "Name" : "الإسم")}
            </div>
            <div className="text-sm text-neutral-400 mt-0.5 truncate">{user?.email}</div>
          </div>
          <div className="relative">
            <div className="w-14 h-14 rounded-full bg-neutral-100 flex items-center justify-center overflow-hidden">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full orange-gradient flex items-center justify-center text-white font-bold text-xl">
                  {(user?.full_name || user?.email || "?")[0].toUpperCase()}
                </div>
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-brand-500 border-2 border-white flex items-center justify-center">
              <Pencil className="w-3 h-3 text-white" />
            </div>
          </div>
        </button>

        {lang !== "en" && <AppTutorialButton />}

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="w-full bg-white rounded-3xl p-4 flex items-center justify-between border border-neutral-100"
          data-testid="settings-theme-toggle"
        >
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${theme === "dark" ? "bg-neutral-800 text-yellow-300" : "bg-brand-50 text-brand-600"}`}>
              {theme === "dark" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </div>
            <div className="text-start">
              <div className="font-bold text-neutral-800">{theme === "dark" ? t("settings.theme_dark") : t("settings.theme_light")}</div>
              <div className="text-xs text-neutral-500">{t("settings.theme_hint")}</div>
            </div>
          </div>
          <div className={`relative w-12 h-7 rounded-full transition ${theme === "dark" ? "bg-brand-500" : "bg-neutral-200"}`}>
            <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${theme === "dark" ? "right-0.5" : "left-0.5"}`} />
          </div>
        </button>

        {/* ══ التغذية والأهداف — hoisted from Profile so the two most-used
            nutrition controls live one tap away instead of two. ══ */}
        <div className="bg-white rounded-3xl border border-neutral-100 overflow-hidden" data-testid="settings-nutrition-section">
          {/* Row 1 — Update goals (opens the full goals editor) */}
          <button
            onClick={() => nav("/app/settings/goals")}
            className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-neutral-50 transition-colors"
            dir="rtl"
            data-testid="settings-goals-row"
          >
            <div className="w-11 h-11 rounded-2xl bg-brand-50 flex items-center justify-center flex-shrink-0">
              <Utensils className="w-5 h-5 text-brand-500" />
            </div>
            <div className="flex-1 min-w-0 text-right">
              <div className="font-bold text-neutral-900 text-sm">{t("settings.goals_row")}</div>
              <div className="mt-1">
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    isManualMode
                      ? "bg-amber-50 text-amber-700 border border-amber-200"
                      : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  }`}
                  dir="rtl"
                >
                  <Sparkles className="w-2.5 h-2.5" />
                  <span>{t("settings.mode_current")} {isManualMode ? t("settings.mode_manual") : t("settings.mode_auto")}</span>
                </span>
              </div>
            </div>
            <ChevronLeft className="w-4 h-4 text-neutral-400 flex-shrink-0" />
          </button>

          {/* Divider */}
          <div className="h-px bg-neutral-100 mx-4" />

          {/* Row 2 — Include-exercise-calories toggle. Layout: icon + title
              + toggle on the top row; description spans the full width
              below so it never squeezes the title into an awkward wrap. */}
          <div className="px-4 py-3.5" dir="rtl">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                <Dumbbell className="w-5 h-5 text-brand-500" />
              </div>
              <div className="flex-1 min-w-0 text-right font-bold text-neutral-900 text-sm leading-snug">
                {t("settings.include_exercise_title")}
              </div>
              <button
                onClick={toggleIncludeExercise}
                data-testid="settings-toggle-include-exercise"
                aria-pressed={includeEx}
                className={`relative w-12 h-7 rounded-full flex-shrink-0 transition-colors ${includeEx ? "bg-brand-500" : "bg-neutral-200"}`}
              >
                <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${includeEx ? "right-0.5" : "right-[calc(100%-1.625rem)]"}`} />
              </button>
            </div>
            <div className="mt-2 pr-14 text-[11.5px] text-neutral-500 leading-relaxed text-right">
              {t("settings.include_exercise_desc")}
              <span className="block text-neutral-400 mt-0.5">{t("settings.include_exercise_note")}</span>
            </div>
          </div>
        </div>

        {/* WhatsApp support card */}
        <button
          onClick={waSupport}
          className="w-full bg-emerald-500 rounded-3xl p-5 text-white flex items-center gap-4 text-start active:scale-[0.99] transition-transform"
          data-testid="settings-whatsapp-card"
        >
          <div className="w-14 h-14 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <div className="font-bold mb-1">{t("settings.support_title")}</div>
            <div className="text-xs text-white/90 leading-relaxed">
              {t("settings.support_desc")}
            </div>
          </div>
        </button>

        {/* Share & Earn (GCC subscribers only — component self-hides) */}
        <ReferralCard />


        {/* Push notification controls — moved to Profile page (personal
            profile hub). Kept here as a comment for discoverability. */}

        {/* Apple HealthKit — moved to Profile page. iOS-only card. */}

        {/* Install as PWA — only shown on web browsers, not inside the
            native iOS/Android app where the user has clearly already
            installed Calofay (they're literally viewing this screen from
            inside the installed app). Language toggle also lives in Profile,
            so we don't duplicate it here. */}
        {!isNativeApp() && (
        <div
          className="w-full rounded-3xl p-5 text-start relative overflow-hidden bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white"
          data-testid="settings-install-card"
        >
          {/* soft brand glow */}
          <div className="pointer-events-none absolute -top-14 -left-10 w-40 h-40 rounded-full bg-brand-500/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-14 -right-10 w-40 h-40 rounded-full bg-brand-500/15 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border border-white/10">
                <Download className="w-6 h-6 text-brand-300" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="font-bold text-sm">{lang === "en" ? "Get the app on your phone" : "حمّل التطبيق على جوالك"}</div>
                  <span className="text-[9px] font-bold bg-brand-500 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">{lang === "en" ? "New" : "جديد"}</span>
                </div>
                <div className="text-[11px] text-white/70 leading-relaxed">
                  {lang === "en"
                    ? "Download from the App Store or Google Play — one tap and it opens."
                    : "حمّله من متجر آبل أو قوقل بلاي — بضغطة واحدة يفتح."}
                </div>
              </div>
            </div>
            {/* Two direct store buttons — no more Safari/Chrome PWA
                explainer. Native apps are LIVE on both stores so we
                send users straight there. */}
            <div className="grid grid-cols-2 gap-2.5">
              <a
                href="https://apps.apple.com/app/id6755807048"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-white text-neutral-900 font-bold text-sm py-3 rounded-xl active:scale-[0.97] hover:bg-white/95 transition-all"
                data-testid="settings-install-ios-btn"
              >
                <Apple className="w-5 h-5" fill="currentColor" />
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-[9px] font-medium text-neutral-500 -mb-0.5">
                    {lang === "en" ? "Download on" : "حمّل من"}
                  </span>
                  <span>App Store</span>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.calofay.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-white text-neutral-900 font-bold text-sm py-3 rounded-xl active:scale-[0.97] hover:bg-white/95 transition-all"
                data-testid="settings-install-android-btn"
              >
                <Smartphone className="w-5 h-5" />
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-[9px] font-medium text-neutral-500 -mb-0.5">
                    {lang === "en" ? "Get it on" : "حمّل من"}
                  </span>
                  <span>Google Play</span>
                </div>
              </a>
            </div>
          </div>
        </div>
        )}

        {/* List items */}
        {/* NOTE: All account rows (goals · recipes · preferences · password ·
            language · support · terms · privacy · reset · delete) live in the
            Profile page (`/app/settings/profile`). Settings is intentionally
            slim now — just the profile card + WhatsApp + share. */}

        {/* ══ تسجيل الخروج ══ Placed at the very bottom, styled as a soft
            destructive row (not an eye-grabbing red brick) so it's easy to
            find when the user is looking for it but doesn't scream danger
            the way "حذف الحساب" does. Confirms first, then wipes the JWT
            via useAuth().logout and bounces to the login screen. */}
        <button
          onClick={() => setConfirmLogout(true)}
          data-testid="settings-logout-btn"
          className="mt-2 w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-white border border-rose-200 text-rose-600 font-bold text-sm active:scale-[0.99] transition-transform"
        >
          <LogOut className="w-4 h-4" />
          <span>{t("settings.logout")}</span>
        </button>

        {/* App version footer — pulls from Capacitor's App plugin so
            support requests come pre-tagged with the exact build the
            user has installed. Hidden on web (no native binary). */}
        {appVersion && (
          <div
            onClick={handleVersionTap}
            className="text-center text-xs text-neutral-400 mt-4 pb-2 select-none cursor-pointer"
            data-testid="settings-version"
          >
            Calofay {appVersion}
          </div>
        )}
      </div>

      {/* AppsFlyer in-app debug panel — 5-tap the version footer to open. */}
      <AppsFlyerDebugModal open={debugOpen} onClose={() => setDebugOpen(false)} />

      {/* Logout confirmation modal — small, focused, one clear action. */}
      {confirmLogout && (
        <div
          className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setConfirmLogout(false)}
          data-testid="logout-confirm-backdrop"
        >
          <div
            className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl"
            dir={lang === "en" ? "ltr" : "rtl"}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-2xl bg-rose-50 flex items-center justify-center">
                <LogOut className="w-5 h-5 text-rose-600" />
              </div>
              <div className="text-lg font-black text-neutral-900">{t("settings.logout_confirm_title")}</div>
            </div>
            <p className="text-sm text-neutral-600 leading-relaxed mb-5">
              {t("settings.logout_confirm_body")}
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={async () => {
                  // Un-scope RevenueCat first so the next user who
                  // signs in on this device doesn't inherit
                  // entitlements. Then clear the token and hard-
                  // reload to /login — a soft nav on iOS
                  // Capacitor leaves the SubscriptionGate mounted
                  // and it flashes the paywall for a beat.
                  try {
                    const { logOutRevenueCat } = await import("@/lib/revenuecat");
                    await logOutRevenueCat();
                  } catch (_) {}
                  try { await logout(); } catch (_) {}
                  try { localStorage.removeItem("calofay_token"); localStorage.removeItem("calofay_refresh"); } catch (_) {}
                  window.location.href = "/login";
                }}
                data-testid="logout-confirm-btn"
                className="w-full py-3 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm transition-colors"
              >
                {t("settings.logout_confirm_yes")}
              </button>
              <button
                onClick={() => setConfirmLogout(false)}
                data-testid="logout-cancel-btn"
                className="w-full py-3 rounded-2xl bg-neutral-100 hover:bg-neutral-200 text-neutral-800 font-bold text-sm transition-colors"
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
